import { useState, useEffect, useCallback } from 'react';
import { format as formatDateFns } from 'date-fns';
import api from '../services/api';

export const useAdminDashboard = (user, refreshKey) => {
  const [adminDashboardData, setAdminDashboardData] = useState(null);
  const [loadingAdminData, setLoadingAdminData] = useState(false);

  const fetchAdminDashboardData = useCallback(async () => {
    if (user?.role_id !== 3) return;
    
    setLoadingAdminData(true);
    try {
      const today = new Date();
      const todayFormatted = formatDateFns(today, 'yyyy-MM-dd');

     const [
          clockLogsRes,
          salesTodayRes,
          topEmployeesRes,
          hourlySalesRes,
          checklistRes,           // ← novo
        ] = await Promise.all([
          api.get('/clock_logs/today'),
          api.get('/sales/today'),
          api.get('/sales/by-employee', { params: { start_date: todayFormatted, end_date: todayFormatted } }),
          api.get('/admin/sales/hourly-today'),
          api.get('/admin/checklist-summary', { params: { date: todayFormatted } }),  // ← novo
        ]);

      const checklistByEmployee = {};
        for (const e of checklistRes.data?.employees || []) {
          checklistByEmployee[e.employee_id] = e;
          // índice secundário por nome para match com clockLogs
          if (e.employee_name) checklistByEmployee[e.employee_name] = e;
        }
        
      const clockLogs = clockLogsRes.data;
      const salesToday = salesTodayRes.data;
      const topEmployees = topEmployeesRes.data;
      const hourlySales = hourlySalesRes.data;

      const totalSalesToday = salesToday.reduce((sum, sale) => sum + sale.total, 0);
      const totalItemsToday = salesToday.reduce((sum, sale) => sum + sale.items, 0);
      const totalSalesCountToday = salesToday.reduce((sum, sale) => sum + sale.sales_count, 0);
      const activeEmployees = clockLogs.filter(log => log.clock_in !== '--:--').length;
      const avgSaleToday = totalSalesCountToday > 0 ? (totalSalesToday / totalSalesCountToday) : 0;

      // /sales/today já vem quebrado por colaboradora + hotel, então dá para
      // agrupar por hotel e guardar quem estava nele sem chamada extra.
      const salesByHotel = salesToday.reduce((acc, sale) => {
        if (!acc[sale.hotel]) {
          acc[sale.hotel] = { name: sale.hotel, value: 0, count: 0, employees: [] };
        }
        acc[sale.hotel].value += sale.total;
        acc[sale.hotel].count += sale.sales_count;
        if (sale.name && !acc[sale.hotel].employees.includes(sale.name)) {
          acc[sale.hotel].employees.push(sale.name);
        }
        return acc;
      }, {});

      // Hotel de cada colaboradora: primeiro pelas vendas do dia, e para quem
      // bateu ponto mas ainda não vendeu, pelo clock-in.
      const hotelsByEmployee = {};
      const addHotel = (name, hotel) => {
        if (!name || !hotel || hotel === 'N/A') return;
        if (!hotelsByEmployee[name]) hotelsByEmployee[name] = [];
        if (!hotelsByEmployee[name].includes(hotel)) hotelsByEmployee[name].push(hotel);
      };
      salesToday.forEach((s) => addHotel(s.name, s.hotel));
      clockLogs.forEach((l) => addHotel(l.employee_name, l.hotel));

      const topEmployeesWithHotel = (topEmployees || []).map((e) => ({
        ...e,
        hotel: (hotelsByEmployee[e.name] || []).join(' • ') || null,
      }));

      setAdminDashboardData({
        totalSalesToday,
        totalItemsToday,
        totalSalesCountToday, 
        avgSaleToday,
        checklistByEmployee,
        activeEmployees,
        salesByHotel: Object.values(salesByHotel),
        topEmployees: topEmployeesWithHotel,
        hourlySales,
        clockLogs,
        salesToday
      });

    } catch (error) {
      console.error('Error fetching admin dashboard data:', error);
    } finally {
      setLoadingAdminData(false);
    }
  }, [user?.role_id]);

  useEffect(() => {
    if (user?.role_id === 3) {
      fetchAdminDashboardData();
    }
  }, [fetchAdminDashboardData, refreshKey]);

  return { adminDashboardData, loadingAdminData, refetch: fetchAdminDashboardData };
};